import React from 'react';
import { X } from 'lucide-react';
import { OnboardingPrinciples, OnboardingTrainingLogistics } from '../../../services/aiIntelligenceService';

const DEVELOPMENT_PRIORITIES = [
  'Technical skills',
  'Tactical awareness',
  'Physical development',
  'Decision making',
  'Creativity',
  'Game understanding',
  'Communication',
  'Confidence building',
  'Teamwork',
  'Individual development',
];

const COACH_OBJECTIVES = [
  'Win matches',
  'Develop players for next level',
  'Build team cohesion',
  'Improve individual technique',
  'Create enjoyment',
  'Develop game intelligence',
  'Build resilience',
  'Prepare for trials/selection',
];

const AGE_GROUPS = [
  'U7', 'U8', 'U9', 'U10', 'U11', 'U12',
  'U13', 'U14', 'U15', 'U16', 'U17', 'U18',
  'Senior',
];

const COMPETITIVE_LEVELS = [
  { value: 'grassroots', label: 'Grassroots' },
  { value: 'development', label: 'Development' },
  { value: 'academy', label: 'Academy' },
  { value: 'semi-professional', label: 'Semi-Professional' },
];

interface PrinciplesStepProps {
  principlesData: OnboardingPrinciples;
  logisticsData: OnboardingTrainingLogistics;
  onPrinciplesChange: (data: OnboardingPrinciples) => void;
  onLogisticsChange: (data: OnboardingTrainingLogistics) => void;
}

export function PrinciplesStep({
  principlesData,
  logisticsData,
  onPrinciplesChange,
  onLogisticsChange,
}: PrinciplesStepProps) {
  const togglePriority = (priority: string) => {
    const current = principlesData.developmentPriorities || [];
    if (current.includes(priority)) {
      onPrinciplesChange({ ...principlesData, developmentPriorities: current.filter((p) => p !== priority) });
    } else {
      onPrinciplesChange({ ...principlesData, developmentPriorities: [...current, priority] });
    }
  };

  const toggleObjective = (objective: string) => {
    const current = principlesData.coachObjectives || [];
    if (current.includes(objective)) {
      onPrinciplesChange({ ...principlesData, coachObjectives: current.filter((o) => o !== objective) });
    } else {
      onPrinciplesChange({ ...principlesData, coachObjectives: [...current, objective] });
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Principles & Logistics</h2>
      <p className="text-sm text-gray-600 mb-6">
        Define your development priorities and training setup.
      </p>

      {/* Development Priorities */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Development Priorities <span className="text-red-500">*</span>
          <span className="text-gray-400 font-normal ml-1">(select at least 1)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {DEVELOPMENT_PRIORITIES.map((priority) => (
            <button
              key={priority}
              type="button"
              onClick={() => togglePriority(priority)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all min-h-[32px] ${
                principlesData.developmentPriorities?.includes(priority)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {priority}
              {principlesData.developmentPriorities?.includes(priority) && (
                <X className="w-3 h-3 inline ml-1" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Coach Objectives */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Coach Objectives <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {COACH_OBJECTIVES.map((objective) => (
            <button
              key={objective}
              type="button"
              onClick={() => toggleObjective(objective)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all min-h-[32px] ${
                principlesData.coachObjectives?.includes(objective)
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {objective}
              {principlesData.coachObjectives?.includes(objective) && (
                <X className="w-3 h-3 inline ml-1" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Season Goals */}
      <div className="mb-8">
        <label htmlFor="season-goals" className="block text-sm font-medium text-gray-700 mb-2">
          Season Goals <span className="text-gray-400 font-normal">(optional, one per line)</span>
        </label>
        <textarea
          id="season-goals"
          value={(principlesData.seasonGoals || []).join('\n')}
          onChange={(e) =>
            onPrinciplesChange({
              ...principlesData,
              seasonGoals: e.target.value.split('\n').filter((g) => g.trim()),
            })
          }
          placeholder="e.g., Improve passing accuracy across the team&#10;Develop set-piece routines"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
      </div>

      {/* Training Logistics divider */}
      <div className="border-t border-gray-200 pt-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Training Logistics</h3>
      </div>

      {/* Age Group & Competitive Level */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="age-group" className="block text-sm font-medium text-gray-700 mb-2">
            Age Group <span className="text-red-500">*</span>
          </label>
          <select
            id="age-group"
            value={logisticsData.ageGroup}
            onChange={(e) => onLogisticsChange({ ...logisticsData, ageGroup: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
          >
            <option value="">Select age group</option>
            {AGE_GROUPS.map((ag) => (
              <option key={ag} value={ag}>{ag}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="competitive-level" className="block text-sm font-medium text-gray-700 mb-2">
            Competitive Level <span className="text-red-500">*</span>
          </label>
          <select
            id="competitive-level"
            value={logisticsData.competitiveLevel}
            onChange={(e) => onLogisticsChange({ ...logisticsData, competitiveLevel: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
          >
            <option value="">Select level</option>
            {COMPETITIVE_LEVELS.map((cl) => (
              <option key={cl.value} value={cl.value}>{cl.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Days per week & Session duration */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="days-per-week" className="block text-sm font-medium text-gray-700 mb-2">
            Training Days per Week <span className="text-red-500">*</span>
          </label>
          <select
            id="days-per-week"
            value={logisticsData.daysPerWeek}
            onChange={(e) => onLogisticsChange({ ...logisticsData, daysPerWeek: parseInt(e.target.value, 10) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
          >
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <option key={d} value={d}>{d} {d === 1 ? 'day' : 'days'}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="session-duration" className="block text-sm font-medium text-gray-700 mb-2">
            Session Duration <span className="text-red-500">*</span>
          </label>
          <select
            id="session-duration"
            value={logisticsData.sessionDurationMinutes}
            onChange={(e) => onLogisticsChange({ ...logisticsData, sessionDurationMinutes: parseInt(e.target.value, 10) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
          >
            <option value={30}>30 minutes</option>
            <option value={45}>45 minutes</option>
            <option value={60}>60 minutes</option>
            <option value={75}>75 minutes</option>
            <option value={90}>90 minutes</option>
            <option value={120}>120 minutes</option>
          </select>
        </div>
      </div>
    </div>
  );
}
